import { EditableField } from '@/common/atoms/EditableField'
import useSaveRoleField from '@/role/hooks/useSaveRoleField'
import { Alert, AlertDescription, AlertIcon, BoxProps } from '@chakra-ui/react'
import { RoleFragment } from '@gql'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleContext } from '../contexts/CIrcleContext'

interface Props extends Omit<BoxProps, 'role'> {
  label: string
  placeholder?: string
  role: RoleFragment
  field: keyof RoleFragment
  initList?: 'bullet' | 'task'
}

export function RoleEditableField({
  label,
  placeholder,
  role,
  field,
  initList,
  ...boxProps
}: Props) {
  const { t } = useTranslation()
  // Who may edit this field, and by which path it is written (GOUV-15)
  const { editable, save } = useSaveRoleField(role, field)

  // Nothing to render outside a circle panel (useSaveRoleField reads the same
  // context to decide the rights, and closes everything when it is missing)
  const circleContext = useContext(CircleContext)
  if (!circleContext) return null

  // Value
  const rawValue = role[field]
  const value = typeof rawValue === 'string' ? rawValue : ''

  return (
    <EditableField
      key={role.id}
      label={label}
      placeholder={placeholder}
      editable={editable}
      value={value}
      initList={initList}
      info={
        role.base ? (
          <Alert status="warning" mt={2}>
            <AlertIcon />
            <AlertDescription>
              {t('RoleEditableField.baseRoleInfo', { role: role.name })}
            </AlertDescription>
          </Alert>
        ) : undefined
      }
      onSave={save}
      {...boxProps}
    />
  )
}
